import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ListSubheader,
  alpha,
  Box,
  List,
  styled,
  Button,
  ListItem
} from '@mui/material';
import { NavLink as RouterLink } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';

import DesignServicesTwoToneIcon from '@mui/icons-material/DesignServicesTwoTone';
import TableChartTwoToneIcon from '@mui/icons-material/TableChartTwoTone';
import AccountCircleTwoToneIcon from '@mui/icons-material/AccountCircleTwoTone';
import BallotTwoToneIcon from '@mui/icons-material/BallotTwoTone';
import BeachAccessTwoToneIcon from '@mui/icons-material/BeachAccessTwoTone';
import EmojiEventsTwoToneIcon from '@mui/icons-material/EmojiEventsTwoTone';
import FilterVintageTwoToneIcon from '@mui/icons-material/FilterVintageTwoTone';
import RedeemTwoToneIcon from '@mui/icons-material/RedeemTwoTone';
import CheckBoxTwoToneIcon from '@mui/icons-material/CheckBoxTwoTone';
import ChromeReaderModeTwoToneIcon from '@mui/icons-material/ChromeReaderModeTwoTone';
import CameraFrontTwoToneIcon from '@mui/icons-material/CameraFrontTwoTone';
import DisplaySettingsTwoToneIcon from '@mui/icons-material/DisplaySettingsTwoTone';
import ContactsTwoToneIcon from '@mui/icons-material/ContactsTwoTone';
import VisibilityTwoToneIcon from '@mui/icons-material/VisibilityTwoTone';
import WifiTetheringTwoToneIcon from '@mui/icons-material/WifiTetheringTwoTone';
import { useAuth } from 'src/util/authenticationProvider';

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: ${theme.colors.alpha.trueWhite[50]};
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.trueWhite[70]};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[30]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }
          
          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.06)};
            color: ${theme.colors.alpha.trueWhite[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
                  'transform',
                  'opacity'
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

function SidebarMenu() {
  const { closeSidebar } = useContext(SidebarContext);
  const { t } = useTranslation();
  const { account } = useAuth();
  const roles = (account?.roles ?? []).map((r) => r.toLowerCase());
  const isOwner = roles.includes('tenant-owner');
  const isAdmin = roles.includes('tenant-admin');
  const isOwnerOrAdmin = isOwner || isAdmin;
  const isTenantEmployeeOnly = roles.includes('tenant-employee') && !isAdmin && !isOwner;

  if (isTenantEmployeeOnly) {
    return (
      <MenuWrapper>
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sidebar.Customer')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/home"
                  startIcon={<DesignServicesTwoToneIcon />}
                >
                  {t('sidebar.DefaultChannel')}
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/overview"
                  startIcon={<TableChartTwoToneIcon />}
                >
                  {t('sidebar.Overview', 'Overview')}
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/management/redeem"
                  startIcon={<RedeemTwoToneIcon />}
                >
                  {t('sidebar.redeem')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
      </MenuWrapper>
    );
  }

  return (
    <>
      <MenuWrapper>
        {/* Customer */}
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sidebar.Customer')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/home"
                  startIcon={<DesignServicesTwoToneIcon />}
                >
                  {t('sidebar.DefaultChannel')}
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/management/redeem"
                  startIcon={<RedeemTwoToneIcon />}
                >
                  {t('sidebar.redeem')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
        {/* Lottery */}
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sidebar.Lottery')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/management/lotteries"
                  startIcon={<BallotTwoToneIcon />}
                >
                  {t('sidebar.Lotteries')}
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/management/active-lotteries"
                  startIcon={<VisibilityTwoToneIcon />}
                >
                  Active Lotteries
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/history/participation"
                  startIcon={<TableChartTwoToneIcon />}
                >
                  {t('sidebar.ParticipationHistory')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
        {/* Management */}
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sidebar.Management')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              {isOwnerOrAdmin && (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/cp/management/users"
                    startIcon={<AccountCircleTwoToneIcon />}
                  >
                    {t('sidebar.Users')}
                  </Button>
                </ListItem>
              )}
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/management/locations"
                  startIcon={<BeachAccessTwoToneIcon />}
                >
                  {t('sidebar.Locations')}
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/management/prizes"
                  startIcon={<EmojiEventsTwoToneIcon />}
                >
                  {t('sidebar.Prizes')}
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/management/media"
                  startIcon={<FilterVintageTwoToneIcon />}
                >
                  {t('sidebar.media')}
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/management/campaigns"
                  startIcon={<ChromeReaderModeTwoToneIcon />}
                >
                  {t('sidebar.Campaigns')}
                </Button>
              </ListItem>

              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/management/leadslists"
                  startIcon={<TableChartTwoToneIcon />}
                >
                  {t('sidebar.LeadsLists', 'Leads List')}
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/management/leads"
                  startIcon={<ContactsTwoToneIcon />}
                >
                  {t('sidebar.Leads', 'Leads')}
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/management/channels"
                  startIcon={<WifiTetheringTwoToneIcon />}
                >
                  Channels
                </Button>
              </ListItem>
              
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/cp/management/privacypolicy"
                  startIcon={<CameraFrontTwoToneIcon />}
                >
                  {t('sidebar.PrivacyPolicy')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
        {/* Settings */}
        {isOwnerOrAdmin && (
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('sidebar.Settings')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/cp/settings/notifications"
                    startIcon={<CheckBoxTwoToneIcon />}
                  >
                    {t('sidebar.NotificationSettings')}
                  </Button>
                </ListItem>
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/cp/settings/system"
                    startIcon={<DisplaySettingsTwoToneIcon />}
                  >
                    {t('sidebar.SystemSettings')}
                  </Button>
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        )}
      </MenuWrapper>
    </>
  );
}

export default SidebarMenu;
